import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Badge,
  Navbar,
  Nav,
  Container,
  NavDropdown,
  Modal,
  Button,
  Form,
} from "react-bootstrap";
import { FaShoppingCart, FaUser, FaCar } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { useLogoutMutation } from "../slices/usersApiSlice";
import { logout } from "../slices/authSlice";
import SearchBox from "./SearchBox";
import logo from "../assets/logo.png";
import {
  loadGarageVehicles,
  loadSelectedGarageVehicleId,
  setSelectedGarageVehicleId,
  loadSelectedGarageVehicle,
  addGarageVehicle,
  updateGarageVehicle,
  removeGarageVehicle,
  clearGarageVehicles,
  isValidVehicle,
} from "../utils/garage";
import { useGetFitmentFacetsQuery } from "../slices/productsApiSlice";

const Header = () => {
  const { cartItems } = useSelector((state) => state.cart);
  const { userInfo } = useSelector((state) => state.auth);
  const isTieredUser = Boolean(userInfo?.tier?.id);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [logoutApiCall] = useLogoutMutation();

  // 🚗 Garage state (multi-vehicle)
  const [garageModal, setGarageModal] = useState(false);
  const [garageVehicles, setGarageVehicles] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [editingId, setEditingId] = useState(""); // "" => adding new
  const [vehicle, setVehicle] = useState(null);

  // Editor fields
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [trim, setTrim] = useState("");

  const hasVehicle = isValidVehicle(vehicle);

  // ✅ Focus Trim support when opened from ProductScreen warning
  const trimSelectRef = useRef(null);
  const [focusTrimNext, setFocusTrimNext] = useState(false);
  const navRef = useRef(null);
  const [isNavOverflowing, setIsNavOverflowing] = useState(false);

  // Load saved vehicles once
  useEffect(() => {
    if (isTieredUser) return;
    const list = loadGarageVehicles();
    setGarageVehicles(list);

    const sel = loadSelectedGarageVehicleId();
    const selected = loadSelectedGarageVehicle(); // fallback to first if needed
    const finalId = selected?.id || sel || "";

    setSelectedId(finalId);
    setEditingId(finalId);
    setVehicle(selected);

    if (selected) {
      setMake(selected.make || "");
      setModel(selected.model || "");
      setYear(selected.year ? String(selected.year) : "");
      setTrim(selected.trim || "");
    }
  }, [isTieredUser]);

  // ============================
  // Fitment dropdown data (Make / Model / Year / Trim)
  // ============================

  // MAKES
  const {
    data: makesData,
    isLoading: isLoadingMakes,
    error: makesError,
  } = useGetFitmentFacetsQuery({});

  const makes = useMemo(
    () => (makesData?.makes ?? []).map((m) => m.make),
    [makesData]
  );

  // MODELS
  const {
    data: modelsData,
    isLoading: isLoadingModels,
    error: modelsError,
  } = useGetFitmentFacetsQuery(make ? { make } : {}, { skip: !make });

  const models = useMemo(
    () => (modelsData?.models ?? []).map((m) => m.model),
    [modelsData]
  );

  // YEARS
  const {
    data: yearsData,
    isLoading: isLoadingYears,
    error: yearsError,
  } = useGetFitmentFacetsQuery(
    make && model ? { make, model } : {},
    { skip: !(make && model) }
  );

  const years = useMemo(() => yearsData?.years ?? [], [yearsData]);

  // TRIMS
  const {
    data: trimsData,
    isLoading: isLoadingTrims,
    error: trimsError,
  } = useGetFitmentFacetsQuery(
    make && model && year ? { make, model, year } : {},
    { skip: !(make && model && year) }
  );

  const trims = useMemo(() => trimsData?.trims ?? [], [trimsData]);

  // ============================
  // Garage helpers
  // ============================

  const startAddNew = () => {
    setEditingId(""); // empty => new
    setVehicle(null);
    setMake("");
    setModel("");
    setYear("");
    setTrim("");
  };

  const selectForEdit = (id) => {
    const list = loadGarageVehicles();
    const v = list.find((x) => x.id === id);
    if (!v) return;

    setSelectedGarageVehicleId(id);
    setSelectedId(id);
    setEditingId(id);
    setVehicle(v);

    setMake(v.make || "");
    setModel(v.model || "");
    setYear(v.year ? String(v.year) : "");
    setTrim(v.trim || "");
  };

  // ============================
  // Garage modal open / save / remove / clear
  // ============================

  const openGarageModal = () => {
    if (isTieredUser) return;
    const list = loadGarageVehicles();
    setGarageVehicles(list);

    const selected = loadSelectedGarageVehicle();
    if (selected) {
      setSelectedId(selected.id);
      setEditingId(selected.id);
      setVehicle(selected);

      setMake(selected.make || "");
      setModel(selected.model || "");
      setYear(selected.year ? String(selected.year) : "");
      setTrim(selected.trim || "");
    } else {
      startAddNew();
    }

    setGarageModal(true);
  };

  // ✅ Listen for ProductScreen trigger: window.dispatchEvent(new CustomEvent("garage:open", { detail: { focus: "trim" }}))
  useEffect(() => {
    if (isTieredUser) return;
    const handler = (e) => {
      // open modal
      openGarageModal();

      // request focus on trim field if asked
      if (e?.detail?.focus === "trim") setFocusTrimNext(true);
    };

    window.addEventListener("garage:open", handler);
    return () => window.removeEventListener("garage:open", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTieredUser, make, model, year, trim]); // safe enough; openGarageModal uses latest state

  // ✅ Focus trim after modal opens + renders
  useEffect(() => {
    if (!garageModal) return;
    if (!focusTrimNext) return;

    const t = setTimeout(() => {
      trimSelectRef.current?.focus?.();
      setFocusTrimNext(false);
    }, 150);

    return () => clearTimeout(t);
  }, [garageModal, focusTrimNext]);

  useEffect(() => {
    const checkOverflow = () => {
      if (!navRef.current) return;
      const { scrollWidth, clientWidth } = navRef.current;
      setIsNavOverflowing(scrollWidth > clientWidth + 1);
    };

    const handleResize = () => checkOverflow();

    window.addEventListener("resize", handleResize);
    const raf = requestAnimationFrame(checkOverflow);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(raf);
    };
  }, [
    cartItems.length,
    userInfo,
    hasVehicle,
    vehicle?.make,
    vehicle?.model,
    vehicle?.year,
    vehicle?.trim,
  ]);

  const saveVehicleHandler = () => {
    const payload = {
      make: (make || "").trim(),
      model: (model || "").trim(),
      year: (year || "").toString().trim(),
      trim: (trim || "").trim(), // optional
    };

    if (!isValidVehicle(payload)) return;

    let saved = null;
    if (editingId) {
      saved = updateGarageVehicle(editingId, payload);
    } else {
      saved = addGarageVehicle(payload);
    }

    const list = loadGarageVehicles();
    setGarageVehicles(list);

    if (saved) {
      setSelectedId(saved.id);
      setEditingId(saved.id);
      setVehicle(saved);
    }

    setGarageModal(false);
  };

  const handleRemove = (id) => {
    const next = removeGarageVehicle(id);
    setGarageVehicles(next);

    const selected = loadSelectedGarageVehicle();
    setVehicle(selected);
    setSelectedId(selected?.id || "");
    setEditingId(selected?.id || "");

    if (selected) {
      setMake(selected.make || "");
      setModel(selected.model || "");
      setYear(selected.year ? String(selected.year) : "");
      setTrim(selected.trim || "");
    } else {
      startAddNew();
    }
  };

  const handleClearGarage = () => {
    clearGarageVehicles();
    setGarageVehicles([]);
    setSelectedId("");
    setEditingId("");
    setVehicle(null);
    setMake("");
    setModel("");
    setYear("");
    setTrim("");
  };

  const logoutHandler = async () => {
    try {
      await logoutApiCall().unwrap();
      dispatch(logout());
      navigate("/login");
    } catch (err) {

    }
  };

  return (
    <header>
      <Navbar bg="primary" variant="dark" expand="lg" collapseOnSelect>
        <Container fluid className="page-shell">
          <Navbar.Brand as={Link} to="/">
            <img
              src={logo}
              alt="KCC Auto Parts"
              style={{ height: "75px", marginRight: "12px" }}
            />
            KCC Parts Distribution 
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav
              ref={navRef}
              className="ms-auto d-flex align-items-center header-nav"
            >
              <SearchBox />
              {!isNavOverflowing && (
                <>
                  <Nav.Link as={Link} to="/page/1">
                    All Products
                  </Nav.Link>
                  <Nav.Link as={Link} to="/about">
                    About Us
                  </Nav.Link>
                  <Nav.Link as={Link} to="/contact">
                    Contact Us
                  </Nav.Link>
                  <Nav.Link as={Link} to="/return-request">
                    Returns
                  </Nav.Link>
                </>
              )}
              {isNavOverflowing && (
                <NavDropdown title="More" id="header-overflow" align="end">
                  <NavDropdown.Item as={Link} to="/page/1">
                    All Products
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/about">
                    About Us
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/contact">
                    Contact Us
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/return-request">
                    Returns
                  </NavDropdown.Item>
                </NavDropdown>
              )}

              {!isTieredUser && (
                <Nav.Link
                  onClick={openGarageModal}
                  className="header-garage-link"
                >
                  <FaCar style={{ marginRight: "5px" }} />
                  <span className="header-garage-label">
                    {hasVehicle
                      ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${
                          vehicle.trim ? " " + vehicle.trim : ""
                        }`
                      : "My Garage"}
                  </span>
                </Nav.Link>
              )}

              <Nav.Link as={Link} to="/cart">
                <FaShoppingCart /> Cart
                {cartItems.length > 0 && (
                  <Badge pill bg="primary" style={{ marginLeft: "5px" }}>
                    {cartItems.reduce((a, c) => a + c.qty, 0)}
                  </Badge>
                )}
              </Nav.Link>

              {userInfo ? (
                <NavDropdown title={userInfo.name} id="username" align="end">
                  <NavDropdown.Item as={Link} to="/profile">
                    Profile
                  </NavDropdown.Item>
                  <NavDropdown.Item onClick={logoutHandler}>
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              ) : (
                <Nav.Link as={Link} to="/login">
                  <FaUser /> Sign In
                </Nav.Link>
              )}

              {userInfo && userInfo.isAdmin && (
                <NavDropdown title="Admin" id="adminmenu" align="end">
                  <NavDropdown.Item as={Link} to="/admin/products">
                    Products
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/quotes">
                    Quotes
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/userlist">
                    Users
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/orderlist">
                    Orders
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/returns">
                    Returns
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/partner-codes">
                    Partner Codes
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/partner-redemptions">
                    Partner Redemptions
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/barcode-lookup">
                    Barcode Scanner
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/eom-billing">
                    EOM Billing
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Link} to="/admin/makes">
                    Vehicle Makes
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/fitments">
                    Vehicle Catalog
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/part-categories">
                    Part Categories
                  </NavDropdown.Item>
                </NavDropdown>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {!isTieredUser && (
        <Modal show={garageModal} onHide={() => setGarageModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>My Garage</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* Saved vehicles list */}
            {garageVehicles.length > 0 && (
              <div className="mb-3">
                <div className="small text-muted mb-1">Saved vehicles</div>

                <div className="d-grid gap-2">
                  {garageVehicles.map((v) => {
                    const label = `${v.year} ${v.make} ${v.model}${
                      v.trim ? " " + v.trim : ""
                    }`;
                    const checked = (editingId || selectedId) === v.id;

                    return (
                      <div
                        key={v.id}
                        className="d-flex align-items-center justify-content-between border rounded p-2"
                      >
                        <Form.Check
                          type="radio"
                          name="garageVehicle"
                          checked={checked}
                          onChange={() => selectForEdit(v.id)}
                          label={label}
                        />
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemove(v.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    );
                  })}
                </div>

                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-2"
                  onClick={startAddNew}
                >
                  + Add another vehicle
                </Button>
              </div>
            )}

            {/* Editor form */}
            <Form>
              {/* Make */}
              <Form.Group className="mb-2">
                <Form.Label>Make</Form.Label>
                {makesError && (
                  <div className="text-danger small mb-1">
                    Failed to load makes
                  </div>
                )}
                <Form.Select
                  value={make}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMake(v);
                    setModel("");
                    setYear("");
                    setTrim("");
                  }}
                  disabled={isLoadingMakes}
                  className="vehicle-input"
                >
                  <option value="">Choose make...</option>
                  {makes.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Model */}
              <Form.Group className="mb-2">
                <Form.Label>Model</Form.Label>
                {modelsError && (
                  <div className="text-danger small mb-1">
                    Failed to load models
                  </div>
                )}
                <Form.Select
                  value={model}
                  onChange={(e) => {
                    const v = e.target.value;
                    setModel(v);
                    setYear("");
                    setTrim("");
                  }}
                  disabled={!make || isLoadingModels}
                  className="vehicle-input"
                >
                  <option value="">Choose model...</option>
                  {models.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Year */}
              <Form.Group className="mb-2">
                <Form.Label>Year</Form.Label>
                {yearsError && (
                  <div className="text-danger small mb-1">
                    Failed to load years
                  </div>
                )}
                <Form.Select
                  value={year}
                  onChange={(e) => {
                    const v = e.target.value;
                    setYear(v);
                    setTrim("");
                  }}
                  disabled={!model || isLoadingYears}
                  className="vehicle-input"
                >
                  <option value="">Choose year...</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Trim (optional) */}
              <Form.Group className="mb-2">
                <Form.Label>Trim (optional)</Form.Label>
                {trimsError && (
                  <div className="text-danger small mb-1">
                    Failed to load trims
                  </div>
                )}
                <Form.Select
                  ref={trimSelectRef}
                  value={trim}
                  onChange={(e) => setTrim(e.target.value)}
                  disabled={!year || isLoadingTrims || trims.length === 0}
                  className="vehicle-input"
                >
                  <option value="">
                    {trims.length ? "No trim (optional)" : "No trims (optional)"}
                  </option>
                  {trims.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Select>

                {/* Optional hint when opened from product fitment warning */}
                {focusTrimNext && (
                  <small className="text-warning d-block mt-1">
                    Tip: add trim so we can confirm fitment more accurately.
                  </small>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>

          <Modal.Footer>
            {garageVehicles.length > 0 && (
              <Button
                variant="danger"
                className="text-white"
                onClick={handleClearGarage}
              >
                Clear Garage
              </Button>
            )}
            <Button
              variant="success"
              className="text-white"
              onClick={saveVehicleHandler}
            >
              {editingId ? "Save Changes" : "Add Vehicle"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </header>
  );
};

export default Header;
